// ─── Users & Auth ───────────────────────────────────────────────────────────

export type TrustLevel = 'sysadmin' | 'senior' | 'dev' | 'junior' | 'viewer';

export type AppName = 'broota' | 'xbroota' | 'nalca' | 'dully' | 'team-ticket' | 'general';

export type AppStatus = 'enabled' | 'suspended' | 'disabled';

export interface AppConfig {
  id:      string;
  label:   string;
  prefix:  string;
  color:   string;
  cssVar:  string;
  status?: AppStatus;
}

// Rol de un usuario dentro de cada app
// viewer = solo lectura | junior = puede pedir | dev = puede enviar a Neo | senior = puede revisar | sysadmin = admin total
export type AppRole = 'none' | 'viewer' | 'junior' | 'dev' | 'senior';

export interface PermissionSet {
  viewOwnRequests:    boolean; // sus propias pedidas
  viewOwnReadOnly:    boolean; // solo lectura (externo)
  viewAreaRequests:   boolean; // pedidas del área/apps asignadas
  viewAllRequests:    boolean; // todas las pedidas
  isAdmin:            boolean; // panel de administración
  requiresValidation: boolean; // pedidas necesitan aprobación de validador
}

// Defaults por nivel — todos configurables por sysadmin
export const DEFAULT_PERMISSIONS: Record<TrustLevel, PermissionSet> = {
  sysadmin: {
    viewOwnRequests: true, viewOwnReadOnly: false,
    viewAreaRequests: true, viewAllRequests: true,
    isAdmin: true, requiresValidation: false,
  },
  senior: {
    viewOwnRequests: true, viewOwnReadOnly: false,
    viewAreaRequests: true, viewAllRequests: false,
    isAdmin: false, requiresValidation: false,
  },
  dev: {
    viewOwnRequests: true, viewOwnReadOnly: false,
    viewAreaRequests: true, viewAllRequests: false,
    isAdmin: false, requiresValidation: false,
  },
  junior: {
    viewOwnRequests: true, viewOwnReadOnly: false,
    viewAreaRequests: false, viewAllRequests: false,
    isAdmin: false, requiresValidation: true,
  },
  viewer: {
    viewOwnRequests: false, viewOwnReadOnly: true,
    viewAreaRequests: false, viewAllRequests: false,
    isAdmin: false, requiresValidation: true,
  },
};

export interface TeamUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  isSysadmin: boolean;                    // acceso al panel de admin del sistema
  appRoles: Record<AppName, AppRole>;     // rol por app — reemplaza trustLevel + appAccess
  trustLevel: TrustLevel;                 // campo legacy/computed: max(appRoles) | 'sysadmin'
  permissions: PermissionSet;
  validatorUid?: string;
  validatorName?: string;
  createdAt: string;
  lastSeenAt?: string;
}

// Helper: calcula el trustLevel efectivo a partir de appRoles + isSysadmin
export function computeTrustLevel(
  isSysadmin: boolean,
  appRoles: Record<AppName, AppRole>,
): TrustLevel {
  if (isSysadmin) return 'sysadmin';
  const roles = Object.values(appRoles) as AppRole[];
  if (roles.includes('senior')) return 'senior';
  if (roles.includes('dev'))    return 'dev';
  if (roles.includes('junior')) return 'junior';
  if (roles.includes('viewer')) return 'viewer';
  return 'viewer';
}

export const DEFAULT_APP_ROLES: Record<AppName, AppRole> = {
  broota: 'none', xbroota: 'none', nalca: 'none', dully: 'none', 'team-ticket': 'none', general: 'none',
};

// ─── Tickets ─────────────────────────────────────────────────────────────────

export type TicketType = 'bug' | 'feature' | 'automation' | 'report' | 'ux' | 'other';

export type TicketStatus =
  | 'created'      // 📋 Creada    — espera que SR/DEV/SYS envíe a Pame
  | 'evaluating'   // 🤖 Evaluando — Pame analiza, DEV/SYS envía a Neo
  | 'development'  // 🔨 Desarrollo — Neo implementa, sale con PR + CI
  | 'review'       // 🔎 Revisión  — PR abierto, sale al mergear a staging
  | 'staging'      // 🧪 Staging   — listo para probar, sale al mergear a main
  | 'production'   // 🚀 Producción — live, estado final
  | 'rejected'     // ❌ Rechazado — descartado
  // ── Legacy (tickets viejos en Notion) ────────────────────────────────────
  | 'received' | 'triage' | 'with-ai' | 'in-progress'
  | 'pame-progress' | 'neo-ready' | 'neo-progress'
  | 'done' | 'discarded';

export type TicketPriority = 'critical' | 'high' | 'medium' | 'low';

export type AIComplexity = 'simple' | 'medium' | 'complex' | 'unrated';

export interface TicketAttachment {
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface Ticket {
  id: string;
  notionId: string;
  title: string;
  description: string;       // Resumen Oski — legible para todos
  technicalContext?: string; // Contexto técnico — detalle para devs (de Pame)
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;
  apps: AppName[];
  dependencies: AppName[];
  aiComplexity: AIComplexity;
  area: string;
  requestedBy: string;       // nombre del solicitante
  agentName?: string;        // agente que procesó (Oski, Pame, Dully)
  assignedDev?: string;
  githubIssueUrl?: string;
  attachments: TicketAttachment[];
  conversationId?: string; // notionId de la conversación Oski que originó el ticket
  createdAt: string;
  updatedAt: string;
}

// ─── Agents ──────────────────────────────────────────────────────────────────

export type AgentName = 'oski' | 'pame' | 'dully' | 'neo';

export interface OskiImageAttachment {
  data:      string;  // base64 (sin el prefijo data:...)
  mediaType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  agent?: AgentName;
  timestamp: string;
  images?: OskiImageAttachment[];
  authorName?: string;
}

export interface AgentStatus {
  name: AgentName;
  status: 'ok' | 'degraded' | 'error';
  latencyMs?: number;
  checkedAt: string;
  services?: {
    anthropic:    'ok' | 'error';
    notion:       'ok' | 'error';
    github_pame:  'ok' | 'error';
    github_neo:   'ok' | 'error';
  };
}

// ─── Audit (sysadmin) ────────────────────────────────────────────────────────
// Schema espejo de team-backend/audit/README.md. Mantener sincronizado.

export type AuditOutcome =
  | 'clean_merge'
  | 'rework_and_merge'
  | 'closed_unmerged'
  | 'reverted';

export type AuditBugClass =
  | 'missing_dependency'
  | 'orphan_import'
  | 'stale_dependency'
  | 'partial_file_read'
  | 'mock_driven_development'
  | 'test_never_ran'
  | 'ssr_unsafe'
  | 'type_escape_hatch'
  | 'markdown_destruction'
  | 'spec_drift'
  | 'context_incompleto_pame'
  | 'otro';

export type AuditResponsibleElement =
  | 'pame'
  | 'neo'
  | 'kika'
  | 'skill:jab-tdd'
  | 'infra'
  | 'humano';

export interface AuditEntry {
  ticket_id: string;
  repo: string;
  pr_number: number;
  pr_url: string;
  timestamp: string;
  outcome: AuditOutcome;
  iterations: number;
  bug_classes: AuditBugClass[];
  responsible_elements: AuditResponsibleElement[];
  evidence?: { files?: string[]; quoted_diff?: string };
  proposed_general_rules?: string[];
  notes?: string;
  tokenUsage?: { input: number; output: number; total: number };
  cost_usd?: number;
}

export interface AuditSummary {
  total: number;
  outcome: Partial<Record<AuditOutcome, number>>;
  bug_classes: Partial<Record<AuditBugClass, number>>;
  responsible_elements: Partial<Record<AuditResponsibleElement, number>>;
  avg_iterations: number | null;
  unknown: {
    outcome: string[];
    bug_classes: string[];
    responsible_elements: string[];
  };
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  total: number;
  summary: AuditSummary;
  parseErrors: { line: number; error: string }[];
  updatedAt: string | null;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

export interface OskiChatRequest {
  message: string;
  history: ChatMessage[];
  userId: string;
  ticketContext?: Partial<Ticket>;
  images?: OskiImageAttachment[];
  pendingSuggestedRequest?: Partial<Ticket>;
  appContext?: string;
}

export interface OskiChatResponse {
  reply: string;
  agent: AgentName;
  suggestedTicket?: Partial<Ticket>;
  suggestedRequest?: Partial<Ticket>;
  createdTicket?: Ticket;
  action?: 'create-ticket' | 'update-ticket' | 'clarify' | 'resolve' | 'create-request';
  ticketError?: string;
}
